/**
 * Каталог пряников — визуалы МП и черновики карточек (этап 6, CONTRACT §2–§3).
 *
 * Ответ `GET /gingerbread/catalog/visuals/:article` разбирается здесь, в одном
 * месте, и устойчив к расхождениям с бэкендом: неизвестные поля игнорируются,
 * неизвестные статусы и площадки не роняют экран, а показываются как есть.
 */

import type { BadgeVariant } from '../../components/ui/Badge'

export type VisualsStatus = 'none' | 'rendering' | 'ready' | 'failed'
export type DraftMarketplace = 'wb' | 'ozon' | 'ym'

export interface VisualSlide {
  index: number
  /** Вид слайда как отдал бэкенд (например `angle`, `top`, `tray`); может быть неизвестным. */
  kind: string
  url: string
}

export interface DraftBlocker {
  code: string
  message: string
}

export interface VisualsDraft {
  /** null — площадка не распознана; тогда смотрим `marketplaceRaw`. */
  marketplace: DraftMarketplace | null
  marketplaceRaw: string
  sellerId: string | null
  sellerName: string | null
  /** Статус черновика как есть: известные подписываются, остальные показываются сырыми. */
  status: string
  cardId: string | null
  blockers: DraftBlocker[]
}

export interface GingerbreadVisuals {
  article: string
  slides: VisualSlide[]
  visualsStatus: VisualsStatus
  drafts: VisualsDraft[]
  updatedAt: string | null
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {}
const text = (value: unknown): string | null => (value == null || value === '' ? null : String(value))

const VISUALS_STATUSES: VisualsStatus[] = ['none', 'rendering', 'ready', 'failed']

/** Написания площадок, которые могут прийти из разных частей системы. */
const MARKETPLACE_ALIASES: Record<string, DraftMarketplace> = {
  wb: 'wb',
  wildberries: 'wb',
  ozon: 'ozon',
  oz: 'ozon',
  ym: 'ym',
  yandex_market: 'ym',
  yandexmarket: 'ym',
  yandex: 'ym',
  market: 'ym',
}

export function toMarketplace(value: unknown): DraftMarketplace | null {
  const key = String(value ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_')
  return MARKETPLACE_ALIASES[key] ?? null
}

function toSlide(raw: unknown, position: number): VisualSlide | null {
  const value = asRecord(raw)
  const url = text(value.url) ?? text(value.publicUrl)
  if (!url) return null
  const index = Number(value.index)
  return { index: Number.isFinite(index) ? index : position, kind: text(value.kind) ?? '', url }
}

function toBlocker(raw: unknown): DraftBlocker | null {
  if (typeof raw === 'string') return raw ? { code: '', message: raw } : null
  const value = asRecord(raw)
  const code = text(value.code) ?? ''
  const message = text(value.message) ?? ''
  if (!code && !message) return null
  return { code, message }
}

function toDraft(raw: unknown): VisualsDraft {
  const value = asRecord(raw)
  const marketplaceRaw = text(value.marketplace) ?? ''
  return {
    marketplace: toMarketplace(marketplaceRaw),
    marketplaceRaw,
    sellerId: text(value.sellerId),
    sellerName: text(value.sellerName),
    status: text(value.status) ?? '',
    cardId: text(value.cardId),
    blockers: (Array.isArray(value.blockers) ? value.blockers : [])
      .map(toBlocker)
      .filter((entry): entry is DraftBlocker => entry != null),
  }
}

/** Разбор ответа бэкенда. На любой мусор отвечает пустым состоянием «Не созданы». */
export function toGingerbreadVisuals(raw: unknown, fallbackArticle = ''): GingerbreadVisuals {
  const value = asRecord(raw)
  const slides = (Array.isArray(value.slides) ? value.slides : [])
    .map(toSlide)
    .filter((entry): entry is VisualSlide => entry != null)
    .sort((a, b) => a.index - b.index)
  const rawStatus = String(value.visualsStatus ?? '').toLowerCase()
  // product-service отдаёт «processing» (gingerbread_visual_runs), экран называет это «rendering».
  const declared = (rawStatus === 'processing' ? 'rendering' : rawStatus) as VisualsStatus
  // Неизвестный статус не показываем наугад: слайды есть — считаем готовыми, нет — не созданными.
  const visualsStatus: VisualsStatus = VISUALS_STATUSES.includes(declared) ? declared : slides.length > 0 ? 'ready' : 'none'
  return {
    article: text(value.article) ?? fallbackArticle,
    slides,
    visualsStatus,
    drafts: (Array.isArray(value.drafts) ? value.drafts : []).map(toDraft),
    updatedAt: text(value.updatedAt),
  }
}

/** jobId запущенной задачи из ответа `POST /gingerbread/catalog/visuals` (202). */
export function toVisualsJobId(raw: unknown): string | null {
  return text(asRecord(raw).jobId)
}

// ── Подписи ─────────────────────────────────────────────────────────

export const VISUALS_STATUS_LABEL: Record<VisualsStatus, string> = {
  none: 'Не созданы',
  rendering: 'Создаются',
  ready: 'Готовы',
  failed: 'Ошибка',
}

export const VISUALS_STATUS_VARIANT: Record<VisualsStatus, BadgeVariant> = {
  none: 'gray',
  rendering: 'blue',
  ready: 'green',
  failed: 'red',
}

export const MARKETPLACE_LABEL: Record<DraftMarketplace, string> = {
  wb: 'Wildberries',
  ozon: 'OZON',
  ym: 'Яндекс Маркет',
}

/** Страницы каталогов площадок: вкладка «Черновики PIM» там. */
export const MARKETPLACE_HREF: Record<DraftMarketplace, string> = {
  wb: '/wildberries',
  ozon: '/ozon',
  ym: '/yandex-market',
}

export function marketplaceLabel(draft: Pick<VisualsDraft, 'marketplace' | 'marketplaceRaw'>): string {
  if (draft.marketplace) return MARKETPLACE_LABEL[draft.marketplace]
  return draft.marketplaceRaw || 'Площадка не указана'
}

// Виды слайдов экшена gbV2RenderSlides (media, gingerbread/v2/slides.py):
// пряник PP0 — cover, dimensions, details; набор PP1 — cover, composition, infographic.
const SLIDE_KIND_LABEL: Record<string, string> = {
  cover: 'Обложка',
  dimensions: 'Размер и вес',
  details: 'Состав и хранение',
  composition: 'Состав набора',
  infographic: 'Вес и срок',
  scene: 'На торте',
}

export function slideLabel(slide: Pick<VisualSlide, 'kind'>, position: number): string {
  const known = SLIDE_KIND_LABEL[slide.kind.toLowerCase()]
  return known ? `${position}. ${known}` : `Слайд ${position}`
}

interface DraftStatusView {
  label: string
  variant: BadgeVariant
}

const DRAFT_STATUS: Record<string, DraftStatusView> = {
  ready: { label: 'Готов к отправке', variant: 'green' },
  ready_to_send: { label: 'Готов к отправке', variant: 'green' },
  created: { label: 'Черновик создан', variant: 'green' },
  draft: { label: 'Черновик создан', variant: 'green' },
  blocked: { label: 'Заблокирован', variant: 'yellow' },
  skipped: { label: 'Уже на площадке', variant: 'gray' },
  not_ready: { label: 'Не готов', variant: 'yellow' },
  queued: { label: 'В очереди', variant: 'blue' },
  pending: { label: 'В очереди', variant: 'blue' },
  processing: { label: 'Создаётся', variant: 'blue' },
  failed: { label: 'Ошибка', variant: 'red' },
  error: { label: 'Ошибка', variant: 'red' },
  none: { label: 'Нет черновика', variant: 'gray' },
}

export function draftStatusView(status: string): DraftStatusView {
  const known = DRAFT_STATUS[status.trim().toLowerCase()]
  if (known) return known
  return { label: status || 'Статус не определён', variant: 'gray' }
}

/** Что делать с известной причиной блокировки. Неизвестные коды показываются сообщением бэкенда. */
export const BLOCKER_HINTS: Record<string, string> = {
  COMMERCIALS_PRICE_MISSING: 'Задайте цену категории (набор или штука) в модуле ценообразования и пересоздайте черновики.',
  OZON_CATEGORY_MISSING: 'Свяжите категорию PP0/PP1 с категорией OZON в справочнике категорий.',
  YM_CATEGORY_MISSING: 'Свяжите категорию PP0/PP1 с категорией Яндекс Маркета в справочнике категорий.',
  MARKETPLACE_SUBJECT_MISSING: 'Укажите предмет Wildberries для категории PP0/PP1 в справочнике категорий.',
  SEO_TEXT_MISSING: 'Сгенерируйте SEO-тексты: нужны название и описание карточки.',
  SEO_PACKAGE_MISSING: 'Сгенерируйте SEO-пакет для товара в модуле SEO.',
  PIPELINE_VISUALS_MISSING: 'Нет визуалов для кабинета: нажмите «Сделать визуалы и черновики» или «Пересоздать».',
}

export const blockerHint = (code: string): string | null => BLOCKER_HINTS[code] ?? null

// ── Подтверждение запуска ───────────────────────────────────────────

/** Текст подтверждения: публикации на маркетплейсы нет, карточки остаются черновиками. */
export function visualsConfirmMessage(count: number): string {
  const noun = count % 10 === 1 && count % 100 !== 11
    ? 'товара'
    : 'товаров'
  return `Будут созданы слайды и черновики карточек для ${count.toLocaleString('ru-RU')} ${noun}. Публикации на маркетплейсы не будет — карточки появятся в «Черновиках ПИМ».`
}
